import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { getViteApiUrl } from '../config/env';
import {
  clearSuppressSessionRestore,
  isSessionRestoreSuppressed,
  setSuppressSessionRestore,
} from '../config/sessionRestore';

interface User {
  id: string;
  name: string;
  email: string;
  role: {
    id: string;
    name: string;
    description?: string;
    permissions?: string[];
  };
  status: string;
  twoFactorEnabled?: boolean;
}

interface AuthContextType {
  /** Siempre null: el JWT va en cookie HttpOnly (`jwt`). */
  token: null;
  user: User | null;
  isAuthenticated: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const REFRESH_INTERVAL = 60 * 60 * 1000;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef<number | null>(null);

  const stopAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const logout = async () => {
    stopAutoRefresh();
    try {
      await fetch(`${getViteApiUrl()}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch {
      /* ignore */
    }
    setUser(null);
    // Tras logout, un F5 no debe rehidratar sesión vía /me si las cookies tardan en borrarse o fallan en el cliente.
    setSuppressSessionRestore();
  };

  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${getViteApiUrl()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        await logout();
        return;
      }

      const data = await response.json();
      if (data.user) {
        setUser(data.user);
      }
    } catch {
      await logout();
    }
  };

  const startAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    refreshIntervalRef.current = window.setInterval(() => {
      void refreshAccessToken();
    }, REFRESH_INTERVAL);
  };

  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    const apiUrl = getViteApiUrl();

    const loadSession = async () => {
      if (isSessionRestoreSuppressed()) {
        try {
          await fetch(`${apiUrl}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
          });
        } catch {
          /* ignore */
        }
        setUser(null);
        setLoading(false);
        return;
      }

      let res = await fetch(`${apiUrl}/auth/me`, { credentials: 'include' });
      if (res.status === 401) {
        const rr = await fetch(`${apiUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });
        if (rr.ok) {
          res = await fetch(`${apiUrl}/auth/me`, { credentials: 'include' });
        }
      }
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        startAutoRefresh();
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    void loadSession();

    return () => stopAutoRefresh();
  }, []);

  const login = (newUser: User) => {
    clearSuppressSessionRestore();
    setUser(newUser);
    startAutoRefresh();
  };

  return (
    <AuthContext.Provider
      value={{
        token: null,
        user,
        isAuthenticated: !!user,
        login,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
